import React, { useCallback, useState } from 'react';
import { Snackbar, Button, IconButton, Box, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import useVersionCheck from '../../hooks/useVersionCheck';
import { applyAppUpdate } from '../../serviceWorkerRegistration';

export default function NewVersionSnackbar() {
  const { t } = useTranslation();
  const updateAvailable = useVersionCheck();
  const [dismissed, setDismissed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const open = updateAvailable && !dismissed;

  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await applyAppUpdate();
    } catch {
      // Reload anyway so the user is not stuck on the prompt.
    }
    window.location.reload();
  }, [refreshing]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up', timeout: { enter: 380, exit: 220 } }}
      sx={{
        bottom: { xs: 12, sm: 16 },
        left: { xs: 12, sm: 16 },
        right: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          maxWidth: 300,
          px: 1.25,
          py: 1,
          borderRadius: 2,
          bgcolor: 'rgba(255, 255, 255, 0.55)',
          color: 'rgba(20, 20, 20, 0.88)',
          backdropFilter: 'blur(14px) saturate(1.4)',
          WebkitBackdropFilter: 'blur(14px) saturate(1.4)',
          border: '1px solid rgba(255, 255, 255, 0.55)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255,255,255,0.6)',
          '@keyframes versionSnackIn': {
            from: { opacity: 0, transform: 'translateY(10px) scale(0.97)' },
            to: { opacity: 1, transform: 'translateY(0) scale(1)' },
          },
          animation: 'versionSnackIn 380ms ease-out',
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.25 }}>
            {t('newVersionAvailable', {
              defaultValue: 'Nouvelle version',
            })}
          </Box>
          <Box sx={{ fontSize: 11, lineHeight: 1.3, color: 'rgba(20, 20, 20, 0.58)', mt: 0.25 }}>
            {t('newVersionAvailableDescription', {
              defaultValue: 'Actualisez pour profiter des dernières améliorations.',
            })}
          </Box>
        </Box>
        <Button
          size="small"
          onClick={handleRefresh}
          disabled={refreshing}
          startIcon={<RefreshIcon sx={{ fontSize: '14px !important' }} />}
          sx={{
            flexShrink: 0,
            minWidth: 0,
            px: 1,
            py: 0.35,
            fontSize: 11.5,
            fontWeight: 600,
            textTransform: 'none',
            color: 'rgba(20, 20, 20, 0.85)',
            bgcolor: 'rgba(255, 255, 255, 0.45)',
            border: '1px solid rgba(255, 255, 255, 0.65)',
            backdropFilter: 'blur(8px)',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.7)' },
          }}
        >
          {t('refreshButton', { defaultValue: 'Actualiser' })}
        </Button>
        <IconButton
          size="small"
          onClick={handleDismiss}
          aria-label="close"
          sx={{
            flexShrink: 0,
            p: 0.25,
            color: 'rgba(20, 20, 20, 0.45)',
            '&:hover': { color: 'rgba(20, 20, 20, 0.75)', bgcolor: 'rgba(255,255,255,0.35)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Snackbar>
  );
}
